import { Component, inject, signal, viewChild, ElementRef } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { COMMON_IMPORTS } from '../../../../Shared/common-imports';
import { MsgService } from '../../../../Shared/Services/msg.service';
import { AutopilotDevicesFacade } from '../../../../../Facades/Intune/autopilot-devices.facade';
import { AutopilotDevicesTableComponent } from '../../Partials/autopilot-devices-table/autopilot-devices-table.component';
import { toSignal } from '@angular/core/rxjs-interop';
import { AuthService } from '../../../../Shared/Services/auth.service';
import { PERMISSIONS } from '../../../../../Enums/permission.enum';

@Component({
  selector: 'av-autopilot-devices-home',
  standalone: true,
  imports: [COMMON_IMPORTS, AutopilotDevicesTableComponent],
  providers: [AutopilotDevicesFacade],
  templateUrl: './autopilot-devices-home.component.html',
  styleUrls: [
    './autopilot-devices-home.component.scss',
    '../../../../../../assets/theme/cutom/_error-container.scss',
  ],
})
export class AutopilotDevicesHomeComponent {
  readonly inputAccept = '.csv,.xlsx,.xls';
  readonly facade = inject(AutopilotDevicesFacade);
  private readonly _msgService = inject(MsgService);
  private readonly _translate = inject(TranslateService);

  readonly selectedFile = signal<File | null>(null);
  readonly isDragging = signal(false);

  readonly fileInput = viewChild<ElementRef<HTMLInputElement>>('fileInput');

  private readonly _authService = inject(AuthService);
  readonly currentUser = toSignal(this._authService.currentUser$, { initialValue: null });
  permissions = PERMISSIONS;

  // --- File Handling ---
  onFileSelected(event: Event) {
    if (this.currentUser()?.hasPermission(PERMISSIONS.AUTOPILOT_DEVICES_IMPORT)) {
      const input = event.target as HTMLInputElement;
      const file = input.files?.[0] ?? null;
      this.handleFile(file);
    } else {
      const msg = this._translate.instant('shared.api.errors.accessDenied');
      this._msgService.showSnackBar(msg, { type: 'error' });
    }
  }

  onDragOver(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging.set(true);
  }

  onDragLeave(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging.set(false);
  }

  onDrop(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging.set(false);

    const file = event.dataTransfer?.files?.[0] ?? null;

    const allowedExtensions = this.inputAccept.split(',');
    if (file && allowedExtensions.some((ext) => file.name.toLowerCase().endsWith(ext))) {
      this.handleFile(file);
    }
  }

  private handleFile(file: File | null) {
    this.selectedFile.set(file);
  }

  browseFile() {
    this.fileInput()?.nativeElement.click();
  }

  async uploadFile() {
    const file = this.selectedFile();
    if (!file) return;
    await this.facade.import(file);
    this.removeFile();
  }

  removeFile() {
    this.selectedFile.set(null);
    const input = this.fileInput()?.nativeElement;
    if (input) input.value = '';
  }

  deleteDevice(id: string) {
    const title = this._translate.instant('app.autopilot_devices.delete.confirm_title');
    const text = this._translate.instant('app.autopilot_devices.delete.confirm_text');

    this._msgService
      .showConfirm(title, text, '', 'warning', 'error')
      .afterClosed()
      .subscribe((confirmed) => {
        if (confirmed) {
          this.facade.delete(id);
        }
      });
  }
}
